import { CheckCircle2, Eye, EyeOff, Loader2, Trash2, Wifi, Zap } from "lucide-react";
import * as React from "react";

import { llmApi } from "@/api/llm";
import { settingsApi, type SecretMeta } from "@/api/settings";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useUIStore } from "@/stores/ui";

/**
 * SecretsManager — admin UI cấu hình tất cả encrypted secrets.
 *
 * P3-B fix: group thành 3 sections (LLM Providers / Odoo Connection /
 * Notification Channels) + Odoo "Test connection" button.
 */
const GROUP_FOR: Record<string, "llm" | "odoo" | "notifications"> = {
  anthropic_api_key: "llm",
  openai_api_key: "llm",
  google_api_key: "llm",
  deepseek_api_key: "llm",
  odoo_url: "odoo",
  odoo_db: "odoo",
  odoo_api_user_login: "odoo",
  odoo_api_key: "odoo",
};

const GROUP_META: Record<
  "llm" | "odoo" | "notifications",
  { title: string; description: string; emoji: string }
> = {
  llm: {
    title: "LLM Providers",
    description:
      "API key cho các nhà cung cấp AI. Provider nào có key sẽ available trong chat selector.",
    emoji: "🤖",
  },
  odoo: {
    title: "Odoo Connection",
    description:
      "URL + DB + Service Account API Key để kết nối ERP. Bắt buộc cấu hình để chat ERP work.",
    emoji: "🔗",
  },
  notifications: {
    title: "Notification Channels",
    description: "Webhooks + tokens cho email / Zalo / Teams / Slack.",
    emoji: "📬",
  },
};

export function SecretsManager(): JSX.Element {
  const [items, setItems] = React.useState<SecretMeta[]>([]);
  const [editingName, setEditingName] = React.useState<string | null>(null);
  const [editValue, setEditValue] = React.useState("");
  const [showValue, setShowValue] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  // Doc 57: LLM test state per-provider
  const [llmTesting, setLlmTesting] = React.useState<string | null>(null);
  const [llmResults, setLlmResults] = React.useState<
    Record<
      string,
      { ok: boolean; text: string; duration_ms: number; error: string | null } | undefined
    >
  >({});
  const [testingOdoo, setTestingOdoo] = React.useState(false);
  const [odooTestResult, setOdooTestResult] = React.useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const pushToast = useUIStore((s) => s.pushToast);

  const refresh = React.useCallback(async () => {
    try {
      const r = await settingsApi.listSecrets();
      setItems(r.items);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi tải danh sách",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  }, [pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const startEdit = (name: string): void => {
    setEditingName(name);
    setEditValue("");
    setShowValue(false);
  };

  const cancelEdit = (): void => {
    setEditingName(null);
    setEditValue("");
  };

  const saveSecret = async (name: string): Promise<void> => {
    if (!editValue.trim()) {
      pushToast({ variant: "warning", title: "Vui lòng nhập giá trị" });
      return;
    }
    setBusy(true);
    try {
      await settingsApi.setSecret(name, editValue.trim());
      pushToast({ variant: "success", title: `Đã lưu ${name}` });
      cancelEdit();
      await refresh();
      if (name.startsWith("odoo_")) setOdooTestResult(null);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  const deleteSecret = async (name: string): Promise<void> => {
    if (!window.confirm(`Xoá secret ${name}? Provider tương ứng sẽ unavailable.`)) {
      return;
    }
    setBusy(true);
    try {
      await settingsApi.deleteSecret(name);
      pushToast({ variant: "success", title: `Đã xoá ${name}` });
      await refresh();
      if (name.startsWith("odoo_")) setOdooTestResult(null);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Xoá thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  // Doc 57: test LLM provider key
  const testLlm = async (
    secretName: string,
  ): Promise<void> => {
    const providerName = secretName.replace(/_api_key$/, "");
    if (!["anthropic", "openai", "google", "deepseek"].includes(providerName)) {
      return;
    }
    setLlmTesting(secretName);
    try {
      const r = await llmApi.testProvider(
        providerName as "anthropic" | "openai" | "google" | "deepseek",
      );
      setLlmResults((prev) => ({
        ...prev,
        [secretName]: {
          ok: r.ok,
          text: r.text,
          duration_ms: r.duration_ms,
          error: r.error,
        },
      }));
    } catch (e) {
      setLlmResults((prev) => ({
        ...prev,
        [secretName]: {
          ok: false,
          text: "",
          duration_ms: 0,
          error: e instanceof Error ? e.message : String(e),
        },
      }));
    } finally {
      setLlmTesting(null);
    }
  };

  const testOdoo = async (): Promise<void> => {
    setTestingOdoo(true);
    setOdooTestResult(null);
    try {
      const res = await settingsApi.testOdoo();
      setOdooTestResult({ ok: res.ok, message: res.message });
      pushToast({
        variant: res.ok ? "success" : "destructive",
        title: res.ok ? "Kết nối OK" : "Kết nối thất bại",
        description: res.message,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setOdooTestResult({ ok: false, message: msg });
      pushToast({
        variant: "destructive",
        title: "Test thất bại",
        description: msg,
      });
    } finally {
      setTestingOdoo(false);
    }
  };

  // Group items
  const grouped: Record<"llm" | "odoo" | "notifications" | "other", SecretMeta[]> = {
    llm: [],
    odoo: [],
    notifications: [],
    other: [],
  };
  for (const it of items) {
    const g = GROUP_FOR[it.name] ?? "notifications";
    grouped[g].push(it);
  }

  return (
    <div className="max-w-3xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold">API Keys / Secrets</h1>
        <p className="text-sm text-muted-foreground">
          Lưu mã hoá AES-256-GCM trong DB. Sau khi lưu, không thể xem lại
          plaintext (chỉ dạng masked).
        </p>
      </header>

      {(["llm", "odoo", "notifications"] as const).map((group) => {
        const list = grouped[group];
        if (list.length === 0) return null;
        const meta = GROUP_META[group];
        return (
          <section key={group} className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <h2 className="text-sm font-semibold flex items-center gap-1.5">
                  <span>{meta.emoji}</span>
                  {meta.title}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {meta.description}
                </p>
              </div>
              {group === "odoo" ? (
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void testOdoo()}
                    disabled={testingOdoo}
                  >
                    <Wifi className="size-3.5 mr-1" />
                    {testingOdoo ? "Đang test..." : "Test connection"}
                  </Button>
                  {odooTestResult ? (
                    <span
                      className={
                        "text-[10px] max-w-[280px] text-right " +
                        (odooTestResult.ok ? "text-emerald-600" : "text-destructive")
                      }
                    >
                      {odooTestResult.ok ? "✓" : "✗"} {odooTestResult.message}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="space-y-2">
              {list.map((it) => {
                const isLlmKey =
                  group === "llm" &&
                  it.name.endsWith("_api_key") &&
                  it.is_set;
                const llmResult = llmResults[it.name];
                return (
                  <div key={it.name} className="space-y-1">
                    <SecretCard
                      item={it}
                      isEditing={editingName === it.name}
                      showValue={showValue}
                      editValue={editValue}
                      onChangeEditValue={setEditValue}
                      onToggleShow={() => setShowValue((v) => !v)}
                      onStart={() => startEdit(it.name)}
                      onCancel={cancelEdit}
                      onSave={() => void saveSecret(it.name)}
                      onDelete={() => void deleteSecret(it.name)}
                      busy={busy}
                      extraAction={
                        isLlmKey ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={llmTesting === it.name}
                            onClick={() => void testLlm(it.name)}
                            title="Test API key (Doc 57)"
                          >
                            {llmTesting === it.name ? (
                              <Loader2 className="size-3 animate-spin mr-1" />
                            ) : (
                              <Zap className="size-3 mr-1" />
                            )}
                            Test
                          </Button>
                        ) : null
                      }
                    />
                    {llmResult ? (
                      <div
                        className={
                          "text-[11px] px-3 py-1.5 rounded border-l-2 " +
                          (llmResult.ok
                            ? "border-emerald-500 bg-emerald-50/40 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-300"
                            : "border-destructive bg-destructive/5 text-destructive")
                        }
                      >
                        {llmResult.ok ? (
                          <>
                            ✓ Hoạt động · {llmResult.duration_ms}ms
                            {llmResult.text ? (
                              <span className="ml-1 text-muted-foreground">
                                · "{llmResult.text.slice(0, 80)}"
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <>✗ {llmResult.error ?? "Test failed"}</>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {grouped.other.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold">Khác</h2>
          {grouped.other.map((it) => (
            <SecretCard
              key={it.name}
              item={it}
              isEditing={editingName === it.name}
              showValue={showValue}
              editValue={editValue}
              onChangeEditValue={setEditValue}
              onToggleShow={() => setShowValue((v) => !v)}
              onStart={() => startEdit(it.name)}
              onCancel={cancelEdit}
              onSave={() => void saveSecret(it.name)}
              onDelete={() => void deleteSecret(it.name)}
              busy={busy}
            />
          ))}
        </section>
      ) : null}
    </div>
  );
}

interface SecretCardProps {
  item: SecretMeta;
  isEditing: boolean;
  showValue: boolean;
  editValue: string;
  onChangeEditValue: (v: string) => void;
  onToggleShow: () => void;
  onStart: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete: () => void;
  busy: boolean;
  /** Doc 57: optional inline action (e.g., "Test" cho LLM keys). */
  extraAction?: React.ReactNode;
}

function SecretCard({
  item: it,
  isEditing,
  showValue,
  editValue,
  onChangeEditValue,
  onToggleShow,
  onStart,
  onCancel,
  onSave,
  onDelete,
  busy,
  extraAction,
}: SecretCardProps): JSX.Element {
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1 flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm">{it.label}</span>
              {it.is_set ? (
                <Badge variant="default" className="text-xs">
                  <CheckCircle2 className="size-3 mr-0.5" />
                  Đã cấu hình
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs">
                  Chưa cấu hình
                </Badge>
              )}
            </div>
            <code className="text-xs text-muted-foreground block">{it.name}</code>
            {it.format_hint ? (
              <span className="text-xs text-muted-foreground">
                Format: <code>{it.format_hint}</code>
              </span>
            ) : null}
          </div>
        </div>

        {isEditing ? (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                type={showValue ? "text" : "password"}
                value={editValue}
                onChange={(e) => onChangeEditValue(e.target.value)}
                placeholder={it.format_hint ?? "Nhập giá trị mới"}
                autoFocus
              />
              <Button variant="ghost" size="icon" onClick={onToggleShow}>
                {showValue ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={onSave} disabled={busy}>
                Lưu
              </Button>
              <Button size="sm" variant="outline" onClick={onCancel}>
                Huỷ
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            {it.is_set ? (
              <code className="flex-1 px-2 py-1 bg-muted rounded text-xs">
                {it.masked_value ?? "***"}
              </code>
            ) : (
              <span className="flex-1 text-xs text-muted-foreground italic">
                Chưa cấu hình.
              </span>
            )}
            {extraAction}
            <Button size="sm" variant="outline" onClick={onStart}>
              {it.is_set ? "Đổi" : "Thêm"}
            </Button>
            {it.is_set ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={onDelete}
                disabled={busy}
              >
                <Trash2 className="size-4" />
              </Button>
            ) : null}
          </div>
        )}

        {it.is_set ? (
          <div className="text-xs text-muted-foreground">
            {it.last_used_at ? (
              <>Lần dùng cuối: {new Date(it.last_used_at).toLocaleString("vi-VN")}</>
            ) : (
              <>Chưa dùng</>
            )}
            {" · "}
            Số lần: {it.use_count}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
